import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Chip,
  alpha,
  useTheme,
  Divider,
  Paper,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

/**
 * Dialog for checking/QC of received batches
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - receiveBatch: The receive record being checked
 * - onCheck: function(checkData) - callback with { receiveId, checkedQty, damagedQty, checkedBy, notes }
 */
function CheckReceivedBatchDialog({ open, onClose, receiveBatch, onCheck }) {
  const theme = useTheme();
  const [checkedQty, setCheckedQty] = useState("");
  const [damagedQty, setDamagedQty] = useState("");
  const [checkedBy, setCheckedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const handleCheck = () => {
    const checked = parseInt(checkedQty, 10) || 0;
    const damaged = parseInt(damagedQty, 10) || 0;
    const received = receiveBatch?.receivedQty || 0;

    // Validation
    if (checked < 0 || damaged < 0) {
      setError("Quantities cannot be negative");
      return;
    }

    // if (Math.abs(checked + damaged - received) > 0.01) {
    //   setError(
    //     `Total checked (${
    //       checked + damaged
    //     }) must equal received quantity (${received})`
    //   );
    //   return;
    // }

    if (!checkedBy.trim()) {
      setError("Please enter who checked the items");
      return;
    }

    // Call the onCheck callback
    onCheck({
      receiveId: receiveBatch.id,
      checkedQty: checked,
      damagedQty: damaged,
      checkedBy: checkedBy.trim(),
      notes: notes.trim(),
    });

    // Reset form
    handleClose();
  };

  const handleClose = () => {
    setCheckedQty("");
    setDamagedQty("");
    setCheckedBy("");
    setNotes("");
    setError("");
    onClose();
  };

  if (!receiveBatch) return null;

  const checked = parseInt(checkedQty, 10) || 0;
  const damaged = parseInt(damagedQty, 10) || 0;
  const total = checked + damaged;
  const received = receiveBatch.receivedQty || 0;
  // How many have already been accounted for in previous partial checks
  const previouslyAccountedFor =
    (receiveBatch.checkRecord?.checkedQty || 0) +
    (receiveBatch.checkRecord?.damagedQty || 0);
  const remainingToCheck = Math.max(0, received - previouslyAccountedFor);
  const remaining = Math.max(0, remainingToCheck - total);
  const isValid = total <= remainingToCheck;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">Check Received Batch</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Receive Batch Information */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Batch ID
              </Typography>
              <Typography variant="body2" fontWeight="600">
                #{receiveBatch.id}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Received Quantity
              </Typography>
              <Typography variant="body2" fontWeight="600" color="primary.main">
                {received} units
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Received By
              </Typography>
              <Typography variant="body2">{receiveBatch.receivedBy}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Received Date
              </Typography>
              <Typography variant="body2">
                {new Date(receiveBatch.receivedDate).toLocaleDateString()}
              </Typography>
            </Grid>
            {previouslyAccountedFor > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Previously Checked (Good)
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">
                    {Math.round(receiveBatch.checkRecord?.checkedQty || 0)} units
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Previously Damaged
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="error.main">
                    {Math.round(receiveBatch.checkRecord?.damagedQty || 0)} units
                  </Typography>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Remaining to Check
              </Typography>
              <Typography variant="body2" fontWeight="600" color="warning.main">
                {Math.round(remainingToCheck)} units
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Good Items (Checked)"
              value={checkedQty}
              onChange={(e) =>
                setCheckedQty(e.target.value.replace(/[^0-9]/g, ""))
              }
              onWheel={(e) => e.target.blur()}
              InputProps={{
                inputProps: { min: 0, step: 1 },
              }}
              helperText="Items that passed QC"
              color="success"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Damaged Items"
              value={damagedQty}
              onChange={(e) =>
                setDamagedQty(e.target.value.replace(/[^0-9]/g, ""))
              }
              onWheel={(e) => e.target.blur()}
              InputProps={{
                inputProps: { min: 0, step: 1 },
              }}
              helperText="Items that failed QC"
              color="error"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Check Summary */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: !isValid
                  ? alpha(theme.palette.error.main, 0.1)
                  : remaining > 0
                  ? alpha(theme.palette.warning.main, 0.1)
                  : alpha(theme.palette.success.main, 0.1),
                borderRadius: 1,
                border: `1px solid ${
                  !isValid
                    ? alpha(theme.palette.error.main, 0.3)
                    : remaining > 0
                    ? alpha(theme.palette.warning.main, 0.3)
                    : alpha(theme.palette.success.main, 0.3)
                }`,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Good (this check)
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">
                    {checked}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Damaged (this check)
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="error.main">
                    {damaged}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Not Yet Tested
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color="warning.main">
                    {Math.max(0, Math.round(remaining))}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    {!isValid ? (
                      <Chip
                        label="Exceeds limit"
                        size="small"
                        color="error"
                        icon={<WarningIcon />}
                        sx={{ height: 20 }}
                      />
                    ) : remaining > 0 ? (
                      <Chip
                        label="Partial"
                        size="small"
                        color="warning"
                        sx={{ height: 20 }}
                      />
                    ) : (
                      <Chip
                        label="Complete"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Checked By"
              value={checkedBy}
              onChange={(e) => setCheckedBy(e.target.value)}
              placeholder="Enter name of person who checked"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe damage type, defects, or other observations..."
            />
          </Grid>
        </Grid>

        <Alert severity={remaining > 0 ? "warning" : "success"} sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>{checked} good items</strong> will be added to
            inventory.
            {damaged > 0 && (
              <>
                {" "}
                <strong>{damaged} damaged items</strong> will be
                tracked for reconstruction.
              </>
            )}
            {remaining > 0 && (
              <>
                {" "}
                <strong>{Math.round(remaining)} items</strong> will remain as
                not yet tested and can be checked later.
              </>
            )}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleCheck}
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          disabled={!isValid || !checkedBy.trim() || total <= 0}
        >
          {remaining > 0 ? "Save Partial Check" : "Complete Check"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CheckReceivedBatchDialog;
